import { createContext, useContext, useEffect, useState, type ReactNode } from 'react'

export type Theme = 'system' | 'light' | 'dark'
export type Palette = 'default' | 'catppuccin' | 'nord' | 'gruvbox' | 'kanagawa' | 'rose_pine' | 'solarized'
type ResolvedTheme = 'light' | 'dark'

interface ThemeContextType {
  theme: Theme
  setTheme: (theme: Theme) => void
  resolvedTheme: ResolvedTheme
  palette: Palette
  setPalette: (palette: Palette) => void
  updateThemeSettings: (theme: Theme, palette: Palette) => void
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined)

function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'dark'
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
}

function resolveTheme(theme: Theme): ResolvedTheme {
  return theme === 'system' ? getSystemTheme() : theme
}

export function ThemeProvider({ children, initialTheme, initialPalette, isAuthenticated = false }: {
  children: ReactNode
  initialTheme: Theme
  initialPalette?: Palette
  isAuthenticated?: boolean
}) {
  const [theme, setThemeState] = useState<Theme>(initialTheme)
  const [palette, setPaletteState] = useState<Palette>(initialPalette ?? 'default')
  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => resolveTheme(initialTheme))

  useEffect(() => {
    const root = document.documentElement
    const resolved = resolveTheme(theme)
    root.setAttribute('data-theme', resolved)
    root.setAttribute('data-palette', palette)
    setResolvedTheme(resolved)
    localStorage.setItem('theme', theme)
    localStorage.setItem('palette', palette)
  }, [theme, palette])

  useEffect(() => {
    if (theme !== 'system') return

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
    const handleChange = () => {
      const resolved = getSystemTheme()
      document.documentElement.setAttribute('data-theme', resolved)
      setResolvedTheme(resolved)
    }

    mediaQuery.addEventListener('change', handleChange)
    return () => mediaQuery.removeEventListener('change', handleChange)
  }, [theme])

  function setTheme(newTheme: Theme) {
    const previousTheme = theme
    setThemeState(newTheme)

    if (isAuthenticated) {
      fetch('/settings/update_theme', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({ theme: newTheme, palette }),
      }).catch(() => {
        setThemeState(previousTheme)
      })
    }
  }

  function setPalette(newPalette: Palette) {
    const previousPalette = palette
    setPaletteState(newPalette)

    if (isAuthenticated) {
      fetch('/settings/update_theme', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({ theme, palette: newPalette }),
      }).catch(() => {
        setPaletteState(previousPalette)
      })
    }
  }

  function updateThemeSettings(newTheme: Theme, newPalette: Palette) {
    const prevTheme = theme
    const prevPalette = palette
    setThemeState(newTheme)
    setPaletteState(newPalette)

    if (isAuthenticated) {
      fetch('/settings/update_theme', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.getAttribute('content') || '',
        },
        body: JSON.stringify({ theme: newTheme, palette: newPalette }),
      }).catch(() => {
        setThemeState(prevTheme)
        setPaletteState(prevPalette)
      })
    }
  }

  return (
    <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme, palette, setPalette, updateThemeSettings }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const context = useContext(ThemeContext)
  if (!context) throw new Error('useTheme must be used within ThemeProvider')
  return context
}

export function syncThemeToServer() {
  const savedTheme = localStorage.getItem('theme') as Theme | null
  const savedPalette = localStorage.getItem('palette') as Palette | null
  if (savedTheme || savedPalette) {
    fetch('/settings/update_theme', {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('[name="csrf-token"]')?.getAttribute('content') || '',
      },
      body: JSON.stringify({
        theme: savedTheme || 'system',
        palette: savedPalette || 'default',
      }),
    }).catch(() => {})
  }
}
